import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  AlertTriangle, 
  TrendingUp, 
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Loader2
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const [reorderAlerts, setReorderAlerts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingLowStock, setExportingLowStock] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, trendRes, alertsRes, depotsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/analytics/sales-trend`, { headers: getAuthHeaders() }),
        axios.get(`${API}/inventory/reorder-alerts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/depots`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setSalesTrend(trendRes.data);
      setReorderAlerts(alertsRes.data);
      setDepots(depotsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Unable to load dashboard data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAuthHeaders]);

  const handleExportLowStock = async () => {
    if (stats?.low_stock_alerts === 0) {
      toast.info('No low stock items to export');
      return;
    }
    
    setExportingLowStock(true);
    try {
      const response = await axios.get(`${API}/dashboard/export-low-stock`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `low_stock_items_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${stats.low_stock_alerts} low stock items`);
    } catch (error) {
      console.error('Error exporting low stock:', error);
      toast.error('Failed to export low stock items');
    } finally {
      setExportingLowStock(false);
    }
  };

  const kpiCards = [
    {
      title: 'Total Stock',
      value: stats?.total_stock?.toLocaleString() || '0',
      icon: Package,
      color: 'bg-blue-500',
      change: '+12%',
      trend: 'up'
    },
    {
      title: 'Low Stock Alerts',
      value: stats?.low_stock_alerts || '0',
      icon: AlertTriangle,
      color: 'bg-red-500',
      change: stats?.low_stock_alerts > 0 ? 'Action Required' : 'All Good',
      trend: stats?.low_stock_alerts > 0 ? 'down' : 'up'
    },
    {
      title: 'Monthly Sales',
      value: stats?.total_sales_this_month?.toLocaleString() || '0',
      icon: TrendingUp,
      color: 'bg-green-500',
      change: '+8%',
      trend: 'up'
    },
    {
      title: 'Active Depots',
      value: stats?.depots_count || '0',
      icon: Building2,
      color: 'bg-purple-500',
      change: 'Operational',
      trend: 'up'
    }
  ];

  // Show loading overlay
  if (loading) {
    return <LoadingOverlay message="Loading dashboard..." />;
  }

  // Show error state
  if (error) {
    return <ErrorState 
      title="Failed to load dashboard" 
      message={error} 
      onRetry={fetchData}
      showDashboardLink={false}
    />;
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Admin Dashboard
        </h1>
        <p className="text-slate-500 mt-1">Overview of inventory and sales across all depots</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.map((card, index) => (
          <Card 
            key={card.title} 
            className="card-hover animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
            data-testid={`kpi-${card.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{card.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2 tabular-nums">
                    {card.value}
                  </p>
                  <div className="flex items-center gap-1 mt-2">
                    {card.trend === 'up' ? (
                      <ArrowUpRight className="w-4 h-4 text-green-500" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-xs font-medium ${card.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {card.change}
                    </span>
                  </div>
                  {/* Export button for Low Stock Alerts */}
                  {card.title === 'Low Stock Alerts' && parseInt(card.value) > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={handleExportLowStock}
                      disabled={exportingLowStock}
                      data-testid="export-low-stock-btn"
                    >
                      {exportingLowStock ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="w-3 h-3 mr-1.5" />
                          Export to Excel
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className={`${card.color} p-3 rounded-xl`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts and Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Trend Chart */}
        <Card className="lg:col-span-2 animate-fade-in animation-delay-200" data-testid="sales-trend-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sales Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesTrend}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0f172a" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#0f172a" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748b' }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748b' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="total_sales" 
                    stroke="#0f172a" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorSales)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Depot Status */}
        <Card className="animate-fade-in animation-delay-300" data-testid="depot-status-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Depot Upload Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {depots.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No depots configured</p>
              ) : (
                depots.map((depot) => (
                  <div 
                    key={depot.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-sm text-slate-900">{depot.name}</p>
                      <p className="text-xs text-slate-500">
                        Last upload: {depot.last_stock_upload 
                          ? new Date(depot.last_stock_upload).toLocaleDateString() 
                          : 'Never'}
                      </p>
                    </div>
                    <Badge 
                      variant={depot.last_stock_upload ? 'default' : 'secondary'}
                      className={depot.last_stock_upload ? 'bg-green-100 text-green-700' : ''}
                    >
                      {depot.last_stock_upload ? 'Active' : 'Pending'}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reorder Alerts Table */}
      <Card className="animate-fade-in animation-delay-400" data-testid="reorder-alerts-table">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Reorder Alerts
            </div>
          </CardTitle>
          <Badge variant="destructive">
            {reorderAlerts.length} items need reorder
          </Badge>
        </CardHeader>
        <CardContent>
          {reorderAlerts.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">All stock levels are healthy!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Item Name</th>
                    <th>Depot</th>
                    <th className="text-right">Current Stock</th>
                    <th className="text-right">Buffer Level</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reorderAlerts.slice(0, 5).map((item) => (
                    <tr key={item.id} className="reorder-alert">
                      <td className="font-mono text-sm">{item.sku}</td>
                      <td>{item.item_name}</td>
                      <td>{item.depot_name}</td>
                      <td className="numeric">{item.quantity}</td>
                      <td className="numeric">{item.buffer_level}</td>
                      <td>
                        <Badge variant="destructive" className="bg-red-100 text-red-700 border-0">
                          Reorder Required
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
